<script setup>
import { onMounted, ref, watch, toRefs, computed } from 'vue';
import { getAttemptDetailsFetch } from '@services/sessionService';

import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Button from 'primevue/button';
import AttempInfo from './components/AttempInfo.vue';

import { useProfileStore, useAuthStore } from '@stores';
import { nameFormat } from '@utils';
import { useDateFormat } from '@vueuse/core';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useBase64 } from '@vueuse/core';
import { Fancybox } from '@fancyapps/ui';

const { t } = useI18n({ useScope: 'global' });

const profileStore = useProfileStore();
const authStore = useAuthStore();
const isLoading = ref(true);
const isLoadingAttempt = ref(true);
const attemptInfo = ref([]);
const props = defineProps(['sessionId', 'userId']);
const showNote = ref(false);
const showTest = ref(true);
const userNote = ref('');
const openedPanels = ref([]);
const toast = useToast();
const apiUrl = import.meta.env.VITE_API_URL;
const biznesportalBasic = import.meta.env.VITE_BIZNESPORTAL_BASIC;

const userRoles = authStore.user?.roles.split(',').map((r) => r.trim());
const isAdmin = userRoles.includes('Admin');
const isSuperAdmin = userRoles.includes('Superadmin');

const { sessionId, userId } = toRefs(props);

const resolver = ({ values }) => {
    const errors = {};
    if (!values.userNote) {
        errors.userNote = [{ message: t('userCreate.required') }];
    }
    return {
        errors,
    };
};

const fetchUserInfo = async () => {
    isLoading.value = true;
    await profileStore.getUserInfo(sessionId.value, userId.value);
    isLoading.value = false;
};

const toggleNote = () => {
    showNote.value = !showNote.value;
};

const openTest = async ({ valid }) => {
    if (!valid) return;
    if (sessionId.value.startsWith('14')) {
        try {
            const response = await axios.post(
                `https://biznes-portal.uz/services/platon-core/api/v1/add/chance/course`,
                {
                    user_id: userId.value,
                    note: userNote.value,
                },
                {
                    headers: {
                        Authorization: biznesportalBasic,
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (response.status == 200) {
                toast.add({ severity: 'success', summary: t('global.dataSent'), life: 3000 });
                showNote.value = false;
                userNote.value = '';
            }
        } catch (error) {
            toast.add({ severity: 'error', summary: t('global.dataSent'), life: 3000 });
        }
    }
};

const passExam = async () => {
    try {
        const response = await axios.post(
            `${apiUrl}/v1/finish/attempt`,
            {
                attempt_id: profileStore.userHistory[0].id,
            },
            {
                headers: {
                    Authorization: `Bearer ${authStore.userToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        if (response.status == 200) {
            toast.add({ severity: 'success', summary: t('global.dataSent'), life: 3000 });
            showTest.value = false;
        }
    } catch (error) {
        toast.add({ severity: 'error', summary: error, life: 3000 });
    }
};
const fileRef = ref();
const fileInput = ref();
const { base64 } = useBase64(fileRef);

const openFancybox = (imageUrl) => {
    if (imageUrl) {
        Fancybox.show([{ src: imageUrl, type: 'image' }]);
    }
};

watch(base64, async (newVal) => {
    if (newVal) {
        changeImage(newVal);
    }
});
const changeImage = async (image) => {
    try {
        const { data } = await axios.put(
            `${apiUrl}/v2/examiner_photo`,
            {
                user_id: userId.value,
                photo: image,
            },
            {
                headers: {
                    Authorization: `Bearer ${authStore.userToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        profileStore.userData.origin_photo = data.data.url;
        profileStore.userData.photo_updated_at = data.data.updated_at;
        toast.add({ severity: 'success', summary: t('global.dataSaved'), life: 3000 });
    } catch (error) {
        toast.add({ severity: 'error', summary: t('profile.errors.' + error.response.data.message), life: 3000 });
    }
};

const handlePanelOpen = (newValue) => {
    if (newValue.length > 0) {
        const panelId = newValue.at(-1);
        getAttemptInfo(panelId);
    }
};

const getAttemptInfo = async (id) => {
    try {
        const data = await getAttemptDetailsFetch(id);

        attemptInfo.value = {
            ...attemptInfo.value,
            [id]: data,
        };
        isLoadingAttempt.value = false;
    } catch (error) {
        console.error(error);
    }
};

onMounted(fetchUserInfo);

watch([sessionId, userId], fetchUserInfo);

const canPassExam = computed(() => {
    const history = profileStore.userHistory[0];
    return history && (history.status === 'IN_PROGRESS' || history.status === 'BLOCKED') && !history.is_sent && showTest.value && sessionId.value == '14665f2b-78b8-4f6d-903e-1c5b29dbf09f';
});

const canToggleNote = computed(() => {
    const history = profileStore.userHistory[0];
    return sessionId.value == '14665f2b-78b8-4f6d-903e-1c5b29dbf09f' && history && (history.status === 'INVALID' || history.status === 'BLOCKED' || history.status === 'FINISHED') && isAdmin;
});
</script>

<template>
    <Spinner v-if="isLoading" />

    <div class="wrapper" v-else>
        <Card class="!bg-[#F7F9FB]">
            <template #content>
                <div class="profileInfo">
                    <div class="info">
                        <Htag tag="h1" class="title titleWithSelect" v-if="profileStore.userData"> {{ nameFormat(profileStore.userData?.full_name) }} </Htag>

                        <div class="counters">
                            <div class="counter">
                                <span class="counter-title">{{ t('global.status') }}</span>
                                <Status v-if="profileStore.userHistory" :status="profileStore.userHistory[0]?.status || profileStore.userLastStatus" size="sm" />
                            </div>
                            <div class="divider" v-if="profileStore.userHistory && profileStore.userHistory[0]?.duration"></div>
                            <div class="counter" v-if="profileStore.userHistory && profileStore.userHistory[0]?.duration">
                                <span class="counter-title">{{ t('global.duration') }}</span>
                                <span class="counter-value">{{ profileStore.userHistory[0]?.duration }}</span>
                            </div>
                            <div class="divider" v-if="profileStore.userHistory && profileStore.userHistory[0]?.duration"></div>
                            <div class="counter" v-if="profileStore.userHistory && profileStore.userHistory[0]?.cheating_count">
                                <span class="counter-title">{{ t('profile.violations') }}</span>
                                <span class="counter-value">{{ profileStore.userHistory[0]?.cheating_count }}</span>
                            </div>
                            <div class="divider" v-if="profileStore.userHistory && profileStore.userHistory[0]?.cheating_count"></div>

                            <div class="counter" v-if="profileStore.userHistory && profileStore.userHistory[0]?.started_at">
                                <span class="counter-title">{{ t('profile.startDate') }}</span>
                                <span class="counter-value">{{ useDateFormat(profileStore.userHistory[0]?.started_at, 'DD.MM.YY, HH:mm').value }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="right-side">
                        <p v-if="profileStore.userData?.photo_updated_at" class="text-sm text-gray-500">{{ t('profile.editedAt') }}: {{ useDateFormat(profileStore.userData?.photo_updated_at, 'DD.MM.YYYY').value }}</p>

                        <div class="right-wrapper">
                            <Button severity="info" v-if="canPassExam" @click="passExam" :label="t('profile.pass')" />
                            <Button severity="info" v-if="canToggleNote" @click="toggleNote" :label="showNote ? t('global.close') : t('global.open')" />

                            <div class="avatar-wrapper">
                                <img :src="profileStore.userData?.origin_photo" :alt="nameFormat(profileStore.userData?.full_name)" class="w-24 h-24 rounded-full object-cover cursor-pointer" @click="openFancybox(profileStore.userData?.origin_photo)" />
                                <Button :label="'Сменить изображение'" icon="pi pi-upload" severity="info" size="small" class="mt-2" @click="$refs.fileInput.click()" />
                                <input ref="fileInput" @change="fileRef = $event.target.files[0]" type="file" class="hidden" accept=".png, .jpg, .jpeg" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
        <Form v-slot="$form" :resolver="resolver" @submit="openTest" class="note-wrapper" v-if="showNote">
            <Textarea name="userNote" v-model.trim="userNote" class="note" :placeholder="t('profile.writeNote')"> </Textarea>
            <Message v-if="$form.userNote?.invalid" severity="error" size="small" variant="simple">{{ $form.userNote.error?.message }}</Message>
            <Button class="mt-2" severity="info" :label="t('global.save')" type="submit" />
        </Form>

        <Htag tag="h2" class="mt-6" v-if="profileStore.userHistory.length">{{ t('profile.attempts') }}</Htag>

        <div class="history">
            <Accordion multiple v-model:value="openedPanels" @update:value="handlePanelOpen">
                <AccordionPanel v-for="item in profileStore.userHistory" :key="item.id" :value="item.id">
                    <AccordionHeader>
                        <div class="accardion-header">
                            <div class="accardion-info">
                                <span>
                                    <sAvatar :src="item.photo" :size="'large'" :name="nameFormat(profileStore.userData?.full_name)" />
                                </span>
                                <span class="item">
                                    {{ t('global.duration') }}:
                                    <span class="item-value">
                                        {{ item.duration ? item.duration : '-' }}
                                    </span>
                                </span>
                                <span class="item">
                                    {{ t('profile.startDate') }}:
                                    <span class="item-value">
                                        {{ item.started_at ? useDateFormat(item.started_at, 'DD.MM.YY').value : '-' }}
                                    </span>
                                </span>
                                <span class="item">
                                    {{ t('global.start') }}:
                                    <span class="item-value">
                                        {{ item.started_at ? useDateFormat(item.started_at, 'HH:mm').value : '-' }}
                                    </span>
                                </span>
                                <span class="item">
                                    {{ t('profile.violations') }}:
                                    <span class="item-value">{{ item.cheating_count ? item.cheating_count : '-' }}</span>
                                </span>

                                <span class="item">
                                    {{ t('profile.score') }}:
                                    <span class="item-value">{{ item.score != null ? item.score : '-' }}</span>
                                </span>
                            </div>
                            <div class="flex items-center w-40">
                                <Status :status="item.status" class="flex-none" />
                            </div>
                        </div>
                    </AccordionHeader>

                    <AccordionContent>
                        <Spinner v-if="isLoadingAttempt" />
                        <AttempInfo v-else :item="{ ...attemptInfo[item.id], originalImg: profileStore.userData?.origin_photo }" :id="item.exam_id" />
                    </AccordionContent>
                </AccordionPanel>
            </Accordion>
        </div>
    </div>
</template>

<style scoped>
@import '@fancyapps/ui/dist/fancybox/fancybox.css';
.wrapper {
    @apply flex gap-4 flex-col;
}

.history {
    @apply flex flex-col gap-2;
}

.info {
    @apply flex gap-4 flex-col;
}

.counters {
    @apply flex gap-7;
}

.divider {
    @apply bg-[#1C1C1C1A] w-[1px] block;
}

.counter-title {
    @apply text-sm;
}

.counter {
    @apply flex gap-1 flex-col;
}

.counter-value {
    @apply text-lg font-semibold;
}

.item {
    @apply flex flex-col text-left text-sm whitespace-nowrap;
}

.item-value {
    @apply text-lg font-semibold;
}

.title {
    @apply text-lg;
}

.profileInfo {
    @apply flex gap-4 items-center justify-between;
}

.note {
    @apply w-full h-32 border rounded resize-none border-solid border-[#ccc] p-2;
}

.note:focus {
    outline: none;
}
.right-side {
    @apply flex flex-col gap-2 items-end;
}

.right-wrapper {
    @apply flex items-center gap-4;
}

.p-accordionheader {
    @apply text-slate-700;
}

.accardion-header {
    @apply flex w-full;
}
.accardion-info {
    @apply grid grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_auto] justify-between items-center gap-4 w-4/5;
}

.avatar-wrapper {
    @apply flex flex-col items-center;
}

@media (max-width: 768px) {
    .profileInfo {
        @apply flex-col gap-4;
    }
    .info {
        @apply w-full;
    }
    .right {
        @apply w-full justify-between;
    }
    .accardion-header {
        @apply flex-col gap-4;
    }
    .accardion-info {
        @apply grid grid-cols-[1fr_1fr] gap-4 w-full;
    }
    .counters {
        @apply flex-col gap-4;
    }
}

@media (max-width: 1024px) {
}
</style>
